import { Injectable } from '@angular/core';
import { NavController } from '@ionic/angular';
import { Network } from '@capacitor/network';
import { CurrentObservationStatusModel } from 'src/app/api/proxy/datapoints/models/current-observation-status-model';
import { ProfileModel } from 'src/app/api/proxy/vitalscore/vitalscore-models';
import { AuthService } from '../auth-service/auth.service';
import { BiometricService } from '../biometric-service/biometric.service';
import { DimensionsService } from '../dimensions-service/dimensions.service';
import { QuestionnaireService } from '../questionnaire-service/questionnaire.service';
import { EmotionService } from '../emotion-service/emotion.service';
import { EventService } from '../events/event.service';
import { FacescanService } from '../face-scan-service/facescan.service';
import { HealthResultsStateService } from '../health-results-state/health-results-state.service';
import { HealthScoreService } from '../health-score-service/health-score.service';
import { MyProfileService } from '../my-profile-service/my-profile.service';
import { OrganizationService } from '../organization-service/organization.service';
import { WearableService } from '../wearable-service/wearable.service';
import { TokenService } from '../token/token.service';
import { LocaleService } from '../locale-service/locale.service';
import { PointsService } from '../points-service/points.service';
import { SecureStorageService } from '../storage/secure-storage.service';
import { StorageKey } from '../storage/models/storage-key.enum';
import { SecureStorageKey } from '../storage/models/secure-storage-key.enum';

@Injectable({
  providedIn: 'root',
})
export class ProfileDataRefreshService {
  private inRefresh = false;
  private pendingForceRefresh = false;
  /** Set when a non-forced refresh arrives while one is already running. */
  private pendingRefresh = false;
  private profileRedirectPending = false;
  private languageChangePendingRefresh = false;
  private beforeRefreshHooks: Array<() => Promise<void>> = [];

  constructor(
    private _profileService: MyProfileService,
    private _organizationService: OrganizationService,
    private _faceScanService: FacescanService,
    private _wearable: WearableService,
    private _emotionService: EmotionService,
    private _biometricService: BiometricService,
    private _dimensionsService: DimensionsService,
    private _questionnaireService: QuestionnaireService,
    private _healthScoreService: HealthScoreService,
    private _authService: AuthService,
    private _tokenService: TokenService,
    private _localeService: LocaleService,
    private _healthResultsState: HealthResultsStateService,
    private _navCtrl: NavController,
    private _pointsService: PointsService,
  ) {
    EventService.refreshProfileData.subscribe((payload) => {
      void this.refresh(payload.forceRefresh ?? false, payload.source);
    });

    EventService.myProfileUpdated.subscribe((profile: ProfileModel) => {
      void this.checkProfileDobRedirect(profile);
    });
  }

  registerBeforeRefresh(hook: () => Promise<void>): () => void {
    this.beforeRefreshHooks.push(hook);
    return () => {
      const index = this.beforeRefreshHooks.indexOf(hook);
      if (index >= 0) {
        this.beforeRefreshHooks.splice(index, 1);
      }
    };
  }

  /** Home bootstrap reads this after profile language save to force a network reload. */
  markLanguageChangePendingRefresh(): void {
    this.languageChangePendingRefresh = true;
  }

  consumeLanguageChangePendingRefresh(): boolean {
    const pending = this.languageChangePendingRefresh;
    this.languageChangePendingRefresh = false;
    return pending;
  }

  /** Drop locale-sensitive caches so the next home refresh uses the new Accept-Language. */
  async clearCachesForLanguageChange(): Promise<void> {
    this._biometricService.clearCachedSummary();
    this._organizationService.invalidateScheduledRefresh();

    await Promise.all([
      this._dimensionsService.clearCache(),
      this._questionnaireService.clearRequestedQuestionnairesCache(),
      this._pointsService.clearAllCache(),
      SecureStorageService.remove(StorageKey.LastProfileRefresh),
      SecureStorageService.remove(StorageKey.BiometricList),
      SecureStorageService.remove(StorageKey.BiometricStatisticsSummary),
      SecureStorageService.remove(StorageKey.WearablesSummary),
      SecureStorageService.remove(StorageKey.WearableStatisticsSummary),
      SecureStorageService.remove(StorageKey.WearablesYesterdaySummary),
      SecureStorageService.remove(StorageKey.EmotionHistory),
      SecureStorageService.remove(StorageKey.MyHealthScore),
      SecureStorageService.remove(StorageKey.NextHealthScoreRefresh),
      SecureStorageService.remove(StorageKey.HealthScoreHistory),
      SecureStorageService.remove(StorageKey.NextFaceScanRefresh),
      SecureStorageService.remove(StorageKey.FaceScanResults),
      SecureStorageService.remove(StorageKey.MediaCategories),
      SecureStorageService.remove(StorageKey.LatestArticles),
      SecureStorageService.remove(StorageKey.MiniAiChatQueryCache),
      SecureStorageService.remove(StorageKey.AiChatQueryCache),
      SecureStorageService.remove(StorageKey.DefualtOrginization),
      SecureStorageService.remove(StorageKey.PatientModel),
      SecureStorageService.remove(StorageKey.QuestionnaireList),
      SecureStorageService.remove(StorageKey.QuestionnaireCache),
      SecureStorageService.remove(StorageKey.QuestionnaireHistory),
      SecureStorageService.remove(SecureStorageKey.PnServices),
      SecureStorageService.remove(SecureStorageKey.LastPnServicesRefresh),
    ]);
  }

  async refresh(forced: boolean, source = 'profile-data-refresh'): Promise<void> {
    // Startup / consent / registration must not pull profile APIs without a valid session.
    const accessToken = await this._tokenService.getTokenAsync();
    if (!accessToken) {
      return;
    }

    await this.loadStaticData();

    if (this.inRefresh && !forced) {
      // Do not drop overlapping home/health-score refresh emits — queue a follow-up.
      this.pendingRefresh = true;
      return;
    }

    const status = await Network.getStatus();
    if (!status.connected) {
      return;
    }

    setTimeout(() => {
      void this.runNetworkRefresh(forced, source);
    }, 300);
  }

  private async runNetworkRefresh(forced: boolean, source: string): Promise<void> {
    const accessToken = await this._tokenService.getTokenAsync();
    if (!accessToken) {
      return;
    }

    if (this.inRefresh && !forced) {
      this.pendingRefresh = true;
      return;
    }

    this.inRefresh = true;
    try {
      if (this.pendingForceRefresh || forced) {
        forced = true;
        this.pendingForceRefresh = false;
      }

      for (const hook of this.beforeRefreshHooks) {
        await hook();
      }

      const tokenExpired = await this._authService.tokenExpired();
      if (tokenExpired || forced) {
        await this._authService.refreshToken();
      }

      const results = await Promise.all([
        this._profileService.GetProfile(forced, false),
        this._organizationService.loadOrganization(`${source} - refreshProfileData`, forced),
        this._faceScanService.getLastScanResult(forced),
        this._wearable.getSummary(forced),
        this._emotionService.getEmotionHistory(forced),
        this._biometricService.getSummary(forced),
        this._dimensionsService.getDimensions(forced),
        this._questionnaireService.getRequestedQuestionnaires(forced),
      ]);

      const biometricSummary = results[5];
      // Network path: always resolve via GetLastScore when biometric payload has no score
      // (cached biometric early-return previously skipped the health-score API entirely).
      await this.resolveHealthScore(biometricSummary, { allowNetwork: true, forced });

      await this._healthScoreService.GetLastScoreHistory(forced);
    } finally {
      this.inRefresh = false;
      if (this.pendingRefresh || this.pendingForceRefresh) {
        const followUpForced = this.pendingForceRefresh;
        this.pendingRefresh = false;
        this.pendingForceRefresh = false;
        void this.refresh(followUpForced, `${source}->pending`);
      }
    }
  }

  private async loadStaticData(): Promise<void> {
    const results = await Promise.all([
      this._profileService.GetProfileStatic(),
      this._organizationService.loadOrganizationStatic(),
      this._faceScanService.getLastScanResultStatic(),
      this._healthScoreService.GetLastScoreHistoryStatic(),
      this._wearable.getSummaryStatic(),
      this._emotionService.getEmotionHistoryStatic(),
      this._biometricService.getSummaryStatic(),
    ]);

    const biometricSummary = results[6];
    await this.resolveHealthScore(biometricSummary, { allowNetwork: false });
  }

  /**
   * Emit health score from biometric payload, else fall back to stored / network health score.
   * Static path must not hit the network; network path must call GetLastScore so a cold start
   * with biometric cache (and no embedded healthScore) still loads the VivaScore.
   */
  private async resolveHealthScore(
    biometricSummary: CurrentObservationStatusModel | null,
    options: { allowNetwork: boolean; forced?: boolean },
  ): Promise<void> {
    if (this._healthResultsState.isInRearrangeMode) {
      return;
    }
    if (biometricSummary?.healthScore) {
      const hs = biometricSummary.healthScore;
      if (typeof hs === 'string') {
        await this._healthScoreService.healthScoreReceived(hs);
      } else {
        await this._healthScoreService.healthScoreReceived(JSON.stringify(hs));
      }
      return;
    }
    if (options.allowNetwork) {
      await this._healthScoreService.GetLastScore(options.forced ?? false);
      return;
    }
    await this._healthScoreService.GetLastScoreStatic();
  }

  private async checkProfileDobRedirect(profile: ProfileModel | null): Promise<void> {
    if (!profile) {
      this.profileRedirectPending = false;
      return;
    }

    if (profile.dateOfBirth) {
      this.profileRedirectPending = false;
      return;
    }

    if (!this.profileRedirectPending) {
      this.profileRedirectPending = true;
      this.pendingForceRefresh = true;
      await this._navCtrl.navigateRoot('home/myProfile');
    }
  }
}
